import { motion } from "framer-motion";
import { CornerRightDown, ArrowUpRight, Mail, FileDown } from "lucide-react";
import { LineReveal, Counter } from "./primitives";
import { HERO_METRICS, CONTACT } from "@/data/content";

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        {/* status label */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#e24a29] opacity-70" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#e24a29]" />
          </span>
          <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#57534e]">
            Open to work · D2C · Startups · Agencies
          </span>
        </motion.div>

        {/* giant tagline */}
        <h1 className="font-display font-black tracking-tighter leading-[0.88] text-[15vw] sm:text-[12vw] lg:text-[9.5vw]">
          <LineReveal delay={0.35}>Marketer across</LineReveal>
          <LineReveal delay={0.5} className="italic">
            <span className="text-[#e24a29]">Social</span>, GTM
          </LineReveal>
          <LineReveal delay={0.65}>&amp; Performance.</LineReveal>
        </h1>

        {/* value prop + CTAs */}
        <div className="mt-10 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-xl text-lg sm:text-xl text-[#57534e] leading-relaxed"
          >
            I scaled a YouTube channel from <span className="text-[#1c1b1a] font-semibold">40K to 60K+ subscribers in
            four months</span> — moving fluidly across organic social, paid ads and growth automation. Not a
            single-lane specialist. A marketer who follows the outcome.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap gap-3 lg:justify-end"
          >
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="hero-email-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1c1b1a] text-[#f5f3ef] text-sm font-semibold hover:bg-[#e24a29] transition-colors duration-300"
            >
              <Mail size={16} /> Email me
            </a>
            <a
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-linkedin-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#1c1b1a] text-sm font-semibold hover:bg-[#1c1b1a] hover:text-[#f5f3ef] transition-colors duration-300"
            >
              LinkedIn <ArrowUpRight size={16} />
            </a>
            <a
              href={CONTACT.resume}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-resume-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#1c1b1a] text-sm font-semibold hover:bg-[#e24a29] hover:border-[#e24a29] hover:text-white transition-colors duration-300"
            >
              <FileDown size={16} /> Résumé
            </a>
          </motion.div>
        </div>

        {/* metric anchor strip */}
        <div className="mt-16 pt-8 border-t border-[rgba(28,27,26,0.12)] grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-4 relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="hidden sm:block absolute -top-1 right-0 text-[#e24a29]"
          >
            <CornerRightDown size={34} strokeWidth={1.5} className="rotate-180 -scale-x-100" />
          </motion.div>
          {HERO_METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15 + i * 0.12, duration: 0.7 }}
            >
              <div className="font-display font-black text-5xl lg:text-6xl tracking-tight">
                <Counter end={m.value} prefix={m.prefix} suffix={m.suffix} />
              </div>
              <div className="mt-2 text-sm text-[#57534e] max-w-[16rem]">{m.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
